(() => {
  'use strict';
  if (window.UniqueMapsModernPreview) {
    window.UniqueMapsModernPreview.init(document);
    return;
  }

  const TOKEN = 'pk.eyJ1IjoibXJzZWIiLCJhIjoiY21tNnRrODNuMGl6NTJzc2VucDl5M3YzYiJ9.O_nyKBonnWpRajKWqlFTqQ';
  const MAPBOX_VERSION = '3.18.0';
  const ROOT = '[data-modern-map-preview]';
  const instances = new Map();
  let libraryPromise;

  function loadMapbox() {
    if (libraryPromise) return libraryPromise;
    libraryPromise = new Promise((resolve, reject) => {
      const base = `https://api.mapbox.com/mapbox-gl-js/v${MAPBOX_VERSION}/mapbox-gl`;
      if (!document.querySelector('link[href*="mapbox-gl.css"]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = `${base}.css`;
        document.head.appendChild(link);
      }
      if (window.mapboxgl) {
        resolve(window.mapboxgl);
        return;
      }
      let script = document.querySelector('script[src*="mapbox-gl.js"]');
      const existing = Boolean(script);
      if (!script) {
        script = document.createElement('script');
        script.src = `${base}.js`;
        script.async = true;
      }
      const timeout = window.setTimeout(() => reject(new Error('Mapbox load timed out')), 15000);
      script.addEventListener('load', () => {
        window.clearTimeout(timeout);
        if (window.mapboxgl) resolve(window.mapboxgl);
        else reject(new Error('Mapbox unavailable'));
      }, { once: true });
      script.addEventListener('error', () => {
        window.clearTimeout(timeout);
        reject(new Error('Mapbox load failed'));
      }, { once: true });
      if (!existing) document.head.appendChild(script);
    });
    return libraryPromise;
  }

  async function locateTown(town, signal) {
    town = (town || '').trim();
    if (!town) {
      // A consistent sample of downtown Manhattan; no geocoding request is needed.
      return { center: [-74.008, 40.707], zoom: 13.5 };
    }
    const params = new URLSearchParams({
      q: town,
      access_token: TOKEN,
      autocomplete: 'false',
      types: 'place,locality,neighborhood',
      limit: '1',
      language: 'en'
    });
    // Use the product's town globally, never the customer's shopping country.
    const response = await fetch(`https://api.mapbox.com/search/geocode/v6/forward?${params}`, { signal });
    if (!response.ok) throw new Error('Town lookup failed');
    const data = await response.json();
    const feature = data.features?.[0];
    const center = feature?.geometry?.coordinates;
    if (!Array.isArray(center) || center.length < 2 ||
        !Number.isFinite(center[0]) || !Number.isFinite(center[1]) ||
        Math.abs(center[0]) > 180 || Math.abs(center[1]) > 90) {
      throw new Error('Town not found');
    }
    return { center: center.slice(0, 2), bbox: feature.properties?.bbox || feature.bbox };
  }

  function mount(root) {
    if (instances.has(root)) return;
    const canvas = root.querySelector('[data-modern-map-canvas]');
    const status = root.querySelector('[data-modern-map-status]');
    const colour = root.querySelector('[data-modern-map-colour]');
    const radios = Array.from(root.querySelectorAll('[data-modern-map-style]'));
    const controller = new AbortController();
    let map;
    let disposed = false;
    let failed = false;
    let currentStyle = '';
    let styleTimeout;
    let resizeObserver;

    function selected() {
      return radios.find(radio => radio.checked) || radios[0];
    }

    function showStatus(message) {
      status.textContent = message;
      status.hidden = !message;
      canvas.setAttribute('aria-hidden', message ? 'true' : 'false');
    }

    function sync() {
      const radio = selected();
      colour.textContent = radio.value;
      const style = radio.dataset.modernMapStyle;
      if (!style) {
        window.clearTimeout(styleTimeout);
        showStatus('Preview unavailable for this colour. Your colour choice will be included with your order.');
        return;
      }
      if (failed) {
        showStatus('The map preview is unavailable. You can still choose your colour.');
        return;
      }
      if (!map) return;
      if (currentStyle !== style) {
        currentStyle = style;
        showStatus('Loading map preview…');
        window.clearTimeout(styleTimeout);
        styleTimeout = window.setTimeout(() => {
          showStatus('The map preview is unavailable. You can still choose your colour.');
        }, 15000);
        map.setStyle(style);
      } else if (map.isStyleLoaded()) {
        showStatus('');
      } else {
        showStatus('Loading map preview…');
      }
    }

    function onReady() {
      window.clearTimeout(styleTimeout);
      if (selected().dataset.modernMapStyle === currentStyle && !failed) showStatus('');
    }

    function onChange(event) {
      if (event.target.matches('[data-modern-map-layout]')) {
        if (window.__umPdpShareRestoring) return;
        const slide = Number(event.target.selectedOptions[0]?.dataset.galleryIndex);
        const gallery = root.closest('[data-product-id]')?.querySelector('.product-slideshow');
        if (gallery && Number.isFinite(slide)) window.Flickity?.data(gallery)?.select(slide);
        return;
      }
      if (radios.includes(event.target)) sync();
    }

    root.addEventListener('change', onChange);
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    instances.set(root, () => {
      disposed = true;
      controller.abort();
      window.clearTimeout(timeout);
      window.clearTimeout(styleTimeout);
      root.removeEventListener('change', onChange);
      resizeObserver?.disconnect();
      map?.remove();
      instances.delete(root);
    });

    async function start() {
      try {
        const [gl, location] = await Promise.all([
          loadMapbox(),
          locateTown(root.dataset.town, controller.signal)
        ]);
        if (disposed) return;
        window.clearTimeout(timeout);
        const defaultStyle = radios.find(radio => radio.dataset.modernMapStyle).dataset.modernMapStyle;
        currentStyle = selected().dataset.modernMapStyle || defaultStyle;
        const neighbourhood = root.dataset.neighbourhood === 'true';
        const options = {
          container: canvas,
          accessToken: TOKEN,
          style: currentStyle,
          center: location.center,
          zoom: location.zoom ?? (neighbourhood ? 13 : 11.5),
          interactive: false,
          attributionControl: false
        };
        const bbox = location.bbox;
        if (Array.isArray(bbox) && bbox.length === 4 && bbox.every(Number.isFinite) &&
            bbox[0] < bbox[2] && bbox[1] < bbox[3]) {
          options.bounds = [[bbox[0], bbox[1]], [bbox[2], bbox[3]]];
          options.fitBoundsOptions = { padding: 24, maxZoom: neighbourhood ? 14 : 12 };
        }
        map = new gl.Map(options);
        map.setZoom(map.getZoom() + 0.5);
        map.addControl(new gl.AttributionControl({ compact: true }), 'bottom-left');
        map.on('idle', onReady);
        map.on('error', () => {
          if (selected().dataset.modernMapStyle) {
            showStatus('The map preview is unavailable. You can still choose your colour.');
          }
        });
        styleTimeout = window.setTimeout(() => {
          if (selected().dataset.modernMapStyle) {
            showStatus('The map preview is unavailable. You can still choose your colour.');
          }
        }, 15000);
        if (window.ResizeObserver) {
          resizeObserver = new ResizeObserver(() => map.resize());
          resizeObserver.observe(canvas);
        }
        sync();
      } catch (error) {
        if (disposed) return;
        failed = true;
        if (selected().dataset.modernMapStyle) {
          showStatus('The map preview is unavailable. You can still choose your colour.');
        }
      } finally {
        window.clearTimeout(timeout);
      }
    }
    sync();
    start();
  }

  function init(scope) {
    if (scope.matches?.(ROOT)) mount(scope);
    scope.querySelectorAll(ROOT).forEach(mount);
  }

  window.UniqueMapsModernPreview = { init };
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((dispose, root) => {
      if (event.target.contains(root)) dispose();
    });
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }
})();
